import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2, Copy, CheckCircle, Clock, AlertCircle } from 'lucide-react';
import { ProgressStepper, PAYMENT_STEPS } from '../components/ProgressStepper';
import { getSession } from '../lib/api';
import { usePaymentParams } from './PaymentRouter';

interface PaymentInstructions {
  type: 'bank_transfer' | 'crypto_transfer';
  // Bank transfer fields (onramp)
  bankName?: string;
  accountNumber?: string;
  accountName?: string;
  reference?: string;
  // Crypto transfer fields (offramp)
  walletAddress?: string;
  network?: string;
  // Common fields
  amountLocal: number;
  localCurrency: string;
  cryptoAmount: number;
  cryptoCurrency: string;
  expiresAt?: string;
}

export function InstructionsPage() {
  const navigate = useNavigate();
  const params = usePaymentParams();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('sessionId');

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [instructions, setInstructions] = useState<PaymentInstructions | null>(null);
  const [copied, setCopied] = useState<string | null>(null);
  const [timeLeft, setTimeLeft] = useState<string | null>(null);

  useEffect(() => {
    if (sessionId) {
      fetchInstructions();
      // Poll for payment status every 10 seconds
      const interval = setInterval(checkPaymentStatus, 10000);
      return () => clearInterval(interval);
    } else {
      setError('No session ID provided');
      setLoading(false);
    }
  }, [sessionId]);

  // Update countdown timer
  useEffect(() => {
    if (instructions?.expiresAt) {
      const interval = setInterval(() => {
        const now = new Date().getTime();
        const expiry = new Date(instructions.expiresAt!).getTime();
        const diff = expiry - now;

        if (diff <= 0) {
          setTimeLeft('Expired');
          clearInterval(interval);
        } else {
          const hours = Math.floor(diff / (1000 * 60 * 60));
          const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
          setTimeLeft(`${hours}h ${minutes}m`);
        }
      }, 1000);

      return () => clearInterval(interval);
    }
  }, [instructions?.expiresAt]);

  async function fetchInstructions() {
    try {
      const token = localStorage.getItem('pouchpay_token') || undefined;
      const data = await getSession(sessionId!, token);

      // The authoritative bank/crypto instruction is the one stashed by the
      // Review page from the create response. GET /v2/sessions/:id also returns
      // it (as `paymentInstruction` / `cryptoInstruction`), but can drop it once
      // the provider webhook mutates providerMeta — so prefer the stash, then
      // fall back to the GET response.
      let stashedInstr: any;
      try {
        const raw = sessionStorage.getItem(`pouchpay_instruction_${sessionId}`);
        if (raw) stashedInstr = JSON.parse(raw).instr;
      } catch { /* ignore */ }

      const pi = stashedInstr || data.paymentInstruction;
      const ci = stashedInstr || data.cryptoInstruction;

      if (data.status === 'COMPLETED') {
        navigate(`/pay/status?sessionId=${sessionId}`);
      } else if (data.type === 'ONRAMP' && pi && pi.accountNumber) {
        // Onramp: bank transfer instructions
        setInstructions({
          type: 'bank_transfer',
          bankName: pi.bankName,
          accountNumber: pi.accountNumber,
          accountName: pi.accountName,
          reference: pi.reference,
          amountLocal: pi.amountLocal ?? data.transaction?.amountLocal ?? 0,
          localCurrency: pi.localCurrency ?? data.transaction?.localCurrency ?? data.countryCode,
          cryptoAmount: pi.cryptoAmount ?? data.transaction?.cryptoAmount ?? 0,
          cryptoCurrency: pi.cryptoCurrency ?? data.transaction?.cryptoCurrency,
          expiresAt: pi.expiresAt
        });
      } else if (data.type === 'OFFRAMP' && (ci?.walletAddress || params?.walletAddress)) {
        // Offramp: crypto deposit instructions
        setInstructions({
          type: 'crypto_transfer',
          walletAddress: ci?.walletAddress ?? params?.walletAddress,
          cryptoAmount: ci?.cryptoAmount ?? data.transaction?.cryptoAmount ?? 0,
          cryptoCurrency: ci?.cryptoCurrency ?? data.transaction?.cryptoCurrency,
          network: ci?.cryptoNetwork ?? params?.network,
          amountLocal: ci?.amountLocal ?? data.transaction?.amountLocal ?? 0,
          localCurrency: ci?.localCurrency ?? data.transaction?.localCurrency ?? data.countryCode
        });
      } else {
        setError('Payment instructions not available yet');
      }
    } catch (err: any) {
      console.error('Fetch instructions error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function checkPaymentStatus() {
    try {
      const token = localStorage.getItem('pouchpay_token');

      const response = await fetch(`https://api.pouchfinance.xyz/v2/sessions/${sessionId}`, {
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        }
      });

      if (response.ok) {
        const data = await response.json();

        if (data.status === 'COMPLETED' || data.status === 'PAYMENT_RECEIVED') {
          navigate(`/pay/status?sessionId=${sessionId}`);
        }
      }
    } catch (err) {
      console.error('Payment status check error:', err);
    }
  }

  async function handleCopy(text: string, field: string) {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(field);
      setTimeout(() => setCopied(null), 2000);
    } catch (err) {
      console.error('Copy failed:', err);
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-brand mx-auto mb-4" />
          <p className="text-gray-600">Loading payment instructions...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-sm p-8 text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-8 h-8 text-red-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Error</h1>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => navigate('/pay')}
            className="px-6 py-2 bg-brand text-white rounded-lg hover:opacity-90 font-medium"
          >
            Start New Payment
          </button>
        </div>
      </div>
    );
  }

  if (!instructions) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Progress Stepper */}
      <ProgressStepper
        steps={PAYMENT_STEPS}
        currentStep="instructions"
        completedSteps={['amount', 'wallet', 'auth', 'kyc', 'review']}
      />

      <div className="max-w-2xl mx-auto px-4 pb-8">
        <h1 className="text-2xl font-bold text-gray-900 text-center mb-2">
          {instructions.type === 'bank_transfer' ? 'Complete Your Payment' : 'Deposit Crypto'}
        </h1>
        <p className="text-center text-gray-600 mb-6">
          {instructions.type === 'bank_transfer'
            ? 'Transfer funds to the account below to complete your purchase'
            : 'Send crypto to the address below to receive your local currency'}
        </p>

        {/* Timer */}
        {timeLeft && timeLeft !== 'Expired' && (
          <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg flex items-center gap-2 justify-center">
            <Clock className="w-5 h-5 text-yellow-600" />
            <span className="text-sm font-medium text-yellow-800">
              Time remaining: {timeLeft}
            </span>
          </div>
        )}

        {/* Main card */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-4">
          {/* Amount to pay */}
          <div className="mb-6 p-6 bg-brand/5 rounded-lg text-center">
            <p className="text-sm text-gray-600 mb-1">Amount to Pay</p>
            <p className="text-3xl font-bold text-gray-900">
              {instructions.localCurrency} {instructions.amountLocal.toLocaleString()}
            </p>
            <p className="text-sm text-gray-500 mt-1">
              ≈ {instructions.cryptoAmount.toFixed(4)} {instructions.cryptoCurrency}
            </p>
          </div>

          {/* Payment details - conditional based on type */}
          <div className="space-y-4">
            {instructions.type === 'bank_transfer' ? (
              <>
                <CopyableField
                  label="Bank Name"
                  value={instructions.bankName!}
                  onCopy={() => handleCopy(instructions.bankName!, 'bank')}
                  copied={copied === 'bank'}
                />

                <CopyableField
                  label="Account Number"
                  value={instructions.accountNumber!}
                  onCopy={() => handleCopy(instructions.accountNumber!, 'account')}
                  copied={copied === 'account'}
                />

                <CopyableField
                  label="Account Name"
                  value={instructions.accountName!}
                  onCopy={() => handleCopy(instructions.accountName!, 'name')}
                  copied={copied === 'name'}
                />

                {instructions.reference && (
                  <CopyableField
                    label="Reference (Optional)"
                    value={instructions.reference}
                    onCopy={() => handleCopy(instructions.reference || '', 'reference')}
                    copied={copied === 'reference'}
                  />
                )}
              </>
            ) : (
              <>
                <CopyableField
                  label="Deposit Address"
                  value={instructions.walletAddress!}
                  onCopy={() => handleCopy(instructions.walletAddress!, 'wallet')}
                  copied={copied === 'wallet'}
                />

                <CopyableField
                  label="Network"
                  value={instructions.network!}
                  onCopy={() => handleCopy(instructions.network!, 'network')}
                  copied={copied === 'network'}
                />

                <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <p className="text-sm text-yellow-800">
                    <strong>Important:</strong> Only send {instructions.cryptoCurrency} on {instructions.network} network.
                    Sending other tokens or using a different network will result in loss of funds.
                  </p>
                </div>
              </>
            )}
          </div>

          {/* Important notice */}
          <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex gap-3">
              <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-blue-800">
                <p className="font-semibold mb-1">Important</p>
                <ul className="list-disc list-inside space-y-1">
                  <li>Transfer the exact amount shown above</li>
                  <li>Use only bank transfer, not USSD or mobile apps that may delay processing</li>
                  <li>Do not close this page until payment is confirmed</li>
                  <li>Your crypto will be sent automatically once payment is received</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Status check */}
        <div className="text-center">
          <button
            onClick={checkPaymentStatus}
            className="text-sm text-brand hover:text-brand-600 font-medium"
          >
            Check Payment Status
          </button>
        </div>

        {/* Footer */}
        <div className="text-center mt-6">
          <p className="text-sm text-gray-500">
            Powered by{' '}
            <a
              href="https://pouchfinance.xyz"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand hover:text-brand-600 font-medium"
            >
              PouchPay
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}

function CopyableField({
  label,
  value,
  onCopy,
  copied
}: {
  label: string;
  value: string;
  onCopy: () => void;
  copied: boolean;
}) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">
        {label}
      </label>
      <div className="flex items-center gap-2">
        <div className="flex-1 px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg font-mono text-sm text-gray-900">
          {value}
        </div>
        <button
          onClick={onCopy}
          className="p-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
        >
          {copied ? (
            <CheckCircle className="w-5 h-5 text-green-600" />
          ) : (
            <Copy className="w-5 h-5 text-gray-600" />
          )}
        </button>
      </div>
    </div>
  );
}
